import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import {
  Map,
  Ruler,
  Building2,
  LineChart,
  FileText,
  Globe,
  Home,
  Trees,
  Waves,
  ClipboardCheck,
  Route,
  ArrowRight,
  CheckCircle2,
  Phone,
} from "lucide-react";

function AnimatedSection({
  children,
  className = "",
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.6, delay, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function PageHeader() {
  return (
    <section className="pt-28 pb-12 bg-gradient-to-br from-primary via-primary to-primary-light relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute -top-20 -right-20 w-64 h-64 rounded-full"
          style={{
            background:
              "radial-gradient(circle, rgba(219,106,64,0.25) 0%, transparent 70%)",
          }}
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.5, 0.8, 0.5],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      </div>

      <div className="section-padding max-w-7xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-left"
        >
          <motion.nav
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="flex items-center gap-2 text-sm mb-4"
          >
            <Link to="/" className="text-white/60 hover:text-accent transition-colors">
              Home
            </Link>
            <span className="text-white/40">/</span>
            <span className="text-accent font-medium">Services</span>
          </motion.nav>

          <motion.span
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, delay: 0.2 }}
            className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 backdrop-blur-sm rounded-full text-accent font-medium text-xs mb-4"
          >
            <span className="w-1.5 h-1.5 bg-accent rounded-full animate-pulse" />
            What We Offer
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="text-3xl md:text-4xl font-bold text-white mb-3"
          >
            Our Services
          </motion.h1>

          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="w-16 h-0.5 bg-accent origin-left mb-4 rounded-full"
          />

          <motion.p
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="text-white/70 max-w-xl text-base"
          >
            Comprehensive land surveying solutions tailored to meet your specific needs, delivered
            with precision and professionalism.
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
}

function ServicesList() {
  const services = [
    {
      icon: Map,
      title: "Topographic Survey",
      shortDesc: "Detailed mapping of terrain features and elevations",
      description:
        "Our topographic surveys provide detailed information about the natural and man-made features of your land. We map elevations, contours, existing structures, vegetation, and utilities to give you a complete picture of your property.",
      features: [
        "Contour mapping with precise elevation data",
        "Identification of natural features (rivers, hills, valleys)",
        "Location of existing structures and utilities",
        "Digital terrain modeling",
        "GIS-compatible data formats",
      ],
      image: "/services/topographic-survey.jpg",
      color: "bg-blue-500",
    },
    {
      icon: Waves,
      title: "Hydrographic Survey",
      shortDesc: "Underwater mapping and waterway assessment",
      description:
        "Our hydrographic surveys map underwater terrain, water depths, and coastal features. We provide accurate data for maritime construction, dredging, and water resource management.",
      features: [
        "Bathymetric mapping",
        "River and lake bed surveys",
        "Coastal and shoreline mapping",
        "Underwater obstacle detection",
        "Water volume calculations",
      ],
      image: "/services/hydrographic-survey.jpg",
      color: "bg-cyan-500",
    },
    {
      icon: ClipboardCheck,
      title: "Asbuilt Survey",
      shortDesc: "Post-construction verification and documentation",
      description:
        "Our as-built surveys document the final constructed positions of buildings, utilities, and infrastructure. We verify that construction matches design plans and provide accurate records for future reference.",
      features: [
        "Post-construction measurement",
        "Utility as-built documentation",
        "Deviation analysis from design",
        "Final record drawings",
        "Compliance verification",
      ],
      image: "/services/asbuilt-survey.jpg",
      color: "bg-slate-500",
    },
    {
      icon: Route,
      title: "Route Survey",
      shortDesc: "Alignment surveys for roads, pipelines, and utilities",
      description:
        "We provide precise route surveys for linear infrastructure projects including roads, railways, pipelines, and power lines. Our surveys establish centerlines, curves, and cross-sections for optimal alignment.",
      features: [
        "Centerline establishment",
        "Horizontal and vertical curve design",
        "Cross-section profiling",
        "Right-of-way determination",
        "Utility conflict analysis",
      ],
      image: "/services/route-survey.jpg",
      color: "bg-amber-500",
    },
    {
      icon: Ruler,
      title: "Boundary Survey",
      shortDesc: "Accurate property line determination and demarcation",
      description:
        "We provide precise boundary surveys to establish or verify property lines. Our licensed surveyors research historical records, locate existing monuments, and set new markers to clearly define your property boundaries.",
      features: [
        "Historical deed research and analysis",
        "Location and verification of boundary markers",
        "Setting of new boundary monuments",
        "Preparation of boundary survey plans",
        "Resolution of boundary disputes",
      ],
      image: "/services/boundary-survey.jpg",
      color: "bg-accent",
    },
    {
      icon: Building2,
      title: "Construction Survey",
      shortDesc: "Precision layout and staking for construction projects",
      description:
        "Our construction surveying services ensure your project is built exactly as designed. We provide layout and staking services for buildings, roads, utilities, and infrastructure projects of all sizes.",
      features: [
        "Building layout and foundation staking",
        "Road and utility alignment",
        "Elevation control and grading stakes",
        "As-built surveys",
        "Structural monitoring during construction",
      ],
      image: "/services/construction-survey.jpg",
      color: "bg-green-500",
    },
    {
      icon: LineChart,
      title: "Subdivision Survey",
      shortDesc: "Land division and new lot creation",
      description:
        "We handle all aspects of land subdivision, from initial planning to final registration. Our team ensures compliance with local regulations and creates accurate plans for new lot creation.",
      features: [
        "Feasibility studies for subdivision",
        "Preparation of subdivision plans",
        "Coordination with planning authorities",
        "Road and utility layout design",
        "Final survey for registration",
      ],
      image: "/services/subdivision-survey.jpg",
      color: "bg-purple-500",
    },
    {
      icon: FileText,
      title: "Cadastral Survey",
      shortDesc: "Official surveys for land registration",
      description:
        "Our cadastral surveys meet all requirements for land registration in Nigeria. We provide legally defensible surveys that clearly define property boundaries for title registration and land administration.",
      features: [
        "Survey for Certificate of Occupancy (C of O)",
        "Deed plan preparation",
        "Registration surveys",
        "Title boundary verification",
        "Land documentation support",
      ],
      image: "/services/cadastral-survey.jpg",
      color: "bg-orange-500",
    },
    {
      icon: Globe,
      title: "GIS & Mapping",
      shortDesc: "Geographic Information Systems and digital mapping",
      description:
        "We offer comprehensive GIS services including data collection, analysis, and mapping. Our digital mapping solutions help you visualize, analyze, and manage spatial data effectively.",
      features: [
        "Spatial data collection and management",
        "Custom map production",
        "GIS database development",
        "Remote sensing and satellite imagery analysis",
        "Web-based mapping solutions",
      ],
      image: "/services/gis-mapping.jpg",
      color: "bg-teal-500",
    },
    {
      icon: Home,
      title: "Site Analysis Survey",
      shortDesc: "Comprehensive site evaluation for development",
      description:
        "Before you break ground, our site analysis surveys provide crucial information about your property's characteristics, constraints, and opportunities for development.",
      features: [
        "Site condition assessment",
        "Drainage and flood risk analysis",
        "Access and easement evaluation",
        "Environmental constraint mapping",
        "Development feasibility reports",
      ],
      image: "/services/site-analysis-survey.jpg",
      color: "bg-indigo-500",
    },
    {
      icon: Trees,
      title: "Agricultural Survey",
      shortDesc: "Farm mapping and agricultural land assessment",
      description:
        "We provide specialized surveying services for agricultural properties, including farm layout planning, irrigation design, and land capability assessment.",
      features: [
        "Farm boundary surveys",
        "Land capability classification",
        "Irrigation system layout",
        "Farm subdivision planning",
        "Agricultural land use planning",
      ],
      image: "/services/agricultural-survey.jpg",
      color: "bg-green-600",
    },
  ];

  return (
    <section className="py-20 bg-white">
      <div className="section-padding max-w-7xl mx-auto">
        <div className="space-y-20">
          {services.map((service, index) => (
            <AnimatedSection key={service.title} delay={0.1}>
              <div
                className={`grid lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? "lg:flex-row-reverse" : ""
                }`}
              >
                <div className={`relative ${index % 2 === 1 ? "lg:order-2" : ""}`}>
                  <div className="rounded-2xl overflow-hidden shadow-xl">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-[350px] object-cover"
                    />
                  </div>
                  <div
                    className={`absolute -bottom-4 ${
                      index % 2 === 1 ? "-right-4" : "-left-4"
                    } w-20 h-20 ${service.color} rounded-xl flex items-center justify-center shadow-lg`}
                  >
                    <service.icon className="w-10 h-10 text-white" />
                  </div>
                </div>

                <div className={index % 2 === 1 ? "lg:order-1" : ""}>
                  <span className="text-accent font-medium text-sm uppercase tracking-wider">
                    Service {String(index + 1).padStart(2, "0")}
                  </span>
                  <h2 className="text-2xl md:text-3xl font-bold text-primary mt-2 mb-4">{service.title}</h2>
                  <p className="text-neutral-600 leading-relaxed mb-6">{service.description}</p>

                  <h4 className="font-semibold text-primary mb-4">What We Provide:</h4>
                  <ul className="space-y-3 mb-8">
                    {service.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                        <span className="text-neutral-600">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-3 bg-accent text-white rounded-full font-medium hover:bg-accent/90 transition-colors">
                    Request This Service
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
}

function ProcessSection() {
  const steps = [
    {
      number: "01",
      title: "Initial Consultation",
      description:
        "We discuss your project requirements, timeline, and budget to understand your specific needs.",
    },
    {
      number: "02",
      title: "Site Visit & Survey",
      description:
        "Our team visits your site to conduct the necessary fieldwork using advanced equipment.",
    },
    {
      number: "03",
      title: "Data Processing",
      description:
        "Collected data is processed and analyzed using specialized software to ensure accuracy.",
    },
    {
      number: "04",
      title: "Report Delivery",
      description:
        "We deliver comprehensive survey reports, plans, and documentation in your preferred format.",
    },
  ];

  return (
    <section className="py-20 bg-neutral-50">
      <div className="section-padding max-w-7xl mx-auto">
        <AnimatedSection className="text-center mb-16">
          <span className="text-accent font-medium">Our Process</span>
          <h2 className="text-2xl md:text-3xl font-bold text-primary mt-2 mb-4">How We Work</h2>
          <p className="text-neutral-600 max-w-2xl mx-auto">
            Our streamlined process ensures efficient delivery of high-quality surveying services.
          </p>
        </AnimatedSection>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((step, index) => (
            <AnimatedSection key={step.number} delay={index * 0.1}>
              <div className="relative">
                <div className="text-6xl font-bold text-accent/20 mb-4">{step.number}</div>
                <h3 className="text-xl font-semibold text-primary mb-3">{step.title}</h3>
                <p className="text-neutral-600 text-sm leading-relaxed">{step.description}</p>
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-8 right-0 w-full h-0.5 bg-gradient-to-r from-accent/30 to-transparent" />
                )}
              </div>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
}

function EquipmentSection() {
  const equipment = [
    "Total Stations",
    "GPS/GNSS Receivers",
    "Drone Surveying",
    "3D Laser Scanners",
    "Digital Levels",
    "GIS Software",
  ];

  return (
    <section className="py-20 bg-primary text-white">
      <div className="section-padding max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <AnimatedSection>
            <span className="text-accent font-medium">Our Equipment</span>
            <h2 className="text-2xl md:text-3xl font-bold text-white mt-2 mb-6">State-of-the-Art Technology</h2>
            <p className="text-neutral-300 leading-relaxed mb-8">
              We invest in the latest surveying technology to ensure the highest level of accuracy
              and efficiency. Our equipment is regularly calibrated and maintained to meet industry
              standards.
            </p>

            <div className="grid grid-cols-2 gap-4">
              {equipment.map((item) => (
                <div key={item} className="flex items-center gap-3 bg-white/10 rounded-lg p-4">
                  <CheckCircle2 className="w-5 h-5 text-accent flex-shrink-0" />
                  <span className="text-sm">{item}</span>
                </div>
              ))}
            </div>
          </AnimatedSection>

          <AnimatedSection delay={0.2}>
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?w=800&auto=format&fit=crop&q=80"
                alt="Surveying Equipment"
                className="w-full h-[400px] object-cover"
              />
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  return (
    <section className="py-20 bg-white">
      <div className="section-padding max-w-7xl mx-auto">
        <AnimatedSection>
          <div className="bg-accent rounded-3xl p-8 md:p-16 text-center relative overflow-hidden">
            <div className="absolute inset-0 opacity-20">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-white rounded-full blur-3xl" />
            </div>

            <div className="relative z-10">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">Need a Custom Surveying Solution?</h2>
              <p className="text-white/90 mb-8 max-w-2xl mx-auto">
                Contact us to discuss your specific requirements. We're ready to provide tailored
                surveying services for your unique project needs.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link
                  to="/contact"
                  className="bg-white text-accent hover:bg-neutral-100 px-8 py-4 rounded-lg font-medium transition-all duration-300 transform hover:scale-105"
                >
                  Contact Us
                </Link>
                <a
                  href="tel:+2348037400677"
                  className="bg-primary/20 hover:bg-primary/30 text-white px-8 py-4 rounded-lg font-medium transition-all backdrop-blur-sm inline-flex items-center gap-2"
                >
                  <Phone className="w-5 h-5" />
                  +234 803 740 0677
                </a>
              </div>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
}

export default function Services() {
  return (
    <main className="min-h-screen">
      <PageHeader />
      <ServicesList />
      <ProcessSection />
      <EquipmentSection />
      <CTASection />
    </main>
  );
}
